/**
 * Session Integrity Hook
 * 
 * Detects session mismatches where localStorage email doesn't match
 * the validated session email. This prevents account confusion.
 */

import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { authLogger } from '@/utils/authLogger';
import { clearAuthCookie, getAuthFromCookie } from '@/utils/cookies';
import { clearValidationCache, setValidationCache } from '@/hooks/useTokenValidation';

// Access the in-memory SWR cache via a probe call: if a fresh result exists,
// useTokenValidation will have populated it. We re-import lazily for the guard.
const isSwrCacheHealthy = (): boolean => {
  try {
    // Heuristic: if localStorage has a token AND a recent role/profile cache,
    // consider SWR healthy enough to skip the integrity audit.
    const hasToken = !!(localStorage.getItem('admin_auth_token') || localStorage.getItem('user_auth_token'));
    if (!hasToken) return false;
    const rolesCache = localStorage.getItem('sirppi_roles_cache');
    if (rolesCache) {
      const parsed = JSON.parse(rolesCache);
      if (parsed?.timestamp && Date.now() - parsed.timestamp < 5 * 60 * 1000) {
        return true;
      }
    }
    return false;
  } catch {
    return false;
  }
};

interface SessionIntegrityOptions {
  /** Check on mount (default: true) */
  checkOnMount?: boolean;
  /** Auto-clear stale tokens (default: true) */
  autoClear?: boolean;
  /** Show user notification on mismatch (default: true) */
  showNotification?: boolean;
}

export const useSessionIntegrity = (options: SessionIntegrityOptions = {}) => {
  const {
    checkOnMount = true,
    autoClear = true,
    showNotification = true
  } = options;

  const hasChecked = useRef(false);

  useEffect(() => {
    if (!checkOnMount || hasChecked.current) return;
    hasChecked.current = true;

    // Quick bail: if no tokens at all, skip entirely (most visitors)
    const hasAnyToken = localStorage.getItem('admin_auth_token') || localStorage.getItem('user_auth_token');
    if (!hasAnyToken) {
      return;
    }

    // Skip entirely when SWR cache is healthy — don't second-guess a known-good session.
    if (isSwrCacheHealthy()) {
      authLogger.debug('SWR cache healthy, skipping session integrity check');
      return;
    }

    const checkIntegrity = async () => {
      authLogger.info('Session integrity check started');

      try {
        // Get emails from localStorage
        const adminEmail = localStorage.getItem('admin_auth_email');
        const userEmail = localStorage.getItem('user_auth_email');
        const adminToken = localStorage.getItem('admin_auth_token');
        const userToken = localStorage.getItem('user_auth_token');

        authLogger.debug('LocalStorage state', {
          hasAdminEmail: !!adminEmail,
          hasUserEmail: !!userEmail,
          hasAdminToken: !!adminToken,
          hasUserToken: !!userToken,
          adminEmail,
          userEmail
        });

        // If no tokens, nothing to check
        if (!adminToken && !userToken) {
          authLogger.debug('No tokens found, skipping integrity check');
          return { isValid: true, mismatch: false };
        }

        // Validate admin session if token exists
        if (adminToken) {
          const adminSession = await getAuthFromCookie('admin');
          
          if (adminSession?.isValid && adminSession.userEmail) {
            const serverEmail = adminSession.userEmail.trim().toLowerCase();
            const localEmail = adminEmail?.trim().toLowerCase();

            // Fresh login: localStorage empty but server has valid session - SYNC instead of clear
            if (!localEmail && serverEmail) {
              authLogger.info('Fresh admin login detected, syncing localStorage', { serverEmail });
              localStorage.setItem('admin_auth_email', serverEmail);
              return { isValid: true, mismatch: false, synced: true };
            }

            // Email differs but server says VALID — server is authoritative.
            // Sync localStorage to match server, never clear the live session.
            if (localEmail && serverEmail !== localEmail) {
              authLogger.warn('Admin email differs from server; syncing to server (no clear)', {
                localEmail,
                serverEmail,
              });
              localStorage.setItem('admin_auth_email', serverEmail);
              return { isValid: true, mismatch: false, synced: true };
            }
          }
        }

        // Validate user session if token exists
        if (userToken) {
          const userSession = await getAuthFromCookie('user');
          
          if (userSession?.isValid && userSession.userEmail) {
            const serverEmail = userSession.userEmail.trim().toLowerCase();
            const localEmail = userEmail?.trim().toLowerCase();

            // Fresh login: localStorage empty but server has valid session - SYNC instead of clear
            if (!localEmail && serverEmail) {
              authLogger.info('Fresh user login detected, syncing localStorage', { serverEmail });
              localStorage.setItem('user_auth_email', serverEmail);
              return { isValid: true, mismatch: false, synced: true };
            }

            // Email differs but server says VALID — server is authoritative.
            // Sync localStorage to match server, never clear the live session.
            if (localEmail && serverEmail !== localEmail) {
              authLogger.warn('User email differs from server; syncing to server (no clear)', {
                localEmail,
                serverEmail,
              });
              localStorage.setItem('user_auth_email', serverEmail);
              return { isValid: true, mismatch: false, synced: true };
            }
          }
        }

        authLogger.info('Session integrity check passed');
        return { isValid: true, mismatch: false };
      } catch (error) {
        authLogger.error('Session integrity check failed', { error: String(error) });
        return { isValid: false, mismatch: false, error: String(error) };
      }
    };

    // Defer until the browser is idle so first interactions and SWR validation
    // have time to complete before we audit. Longer defer prevents racing the
    // initial server validation on hard refresh.
    const ric = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined;
    if (ric) {
      ric(() => { checkIntegrity(); }, { timeout: 8000 });
    } else {
      setTimeout(() => { checkIntegrity(); }, 4000);
    }
  }, [checkOnMount, autoClear, showNotification]);

  /**
   * Manually trigger integrity check
   */
  const forceCheck = async () => {
    hasChecked.current = false;
    // The useEffect will re-run on next render
  };

  return { forceCheck };
};

export default useSessionIntegrity;
